import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { ChannelApiIntegrationPanel } from "@/pages/stammdaten/schnittstellen/ChannelApiIntegrationPanel";
import { DeviceServiceIntegrationPanel } from "@/pages/stammdaten/schnittstellen/DeviceServiceIntegrationPanel";
import { EmailIntegrationPanel } from "@/pages/stammdaten/schnittstellen/EmailIntegrationPanel";
import { BarrierIntegrationPanel } from "@/pages/stammdaten/schnittstellen/BarrierIntegrationPanel";
import { FernausleseIntegrationPanel } from "@/pages/stammdaten/schnittstellen/FernausleseIntegrationPanel";
import { GooglePlacesIntegrationPanel } from "@/pages/stammdaten/schnittstellen/GooglePlacesIntegrationPanel";
import { InfoScreenProvidersPanel } from "@/pages/stammdaten/schnittstellen/InfoScreenProvidersPanel";
import { IntegrationPageLayout } from "@/pages/stammdaten/schnittstellen/IntegrationPageLayout";
import { LprIntegrationPanel } from "@/pages/stammdaten/schnittstellen/LprIntegrationPanel";
import { PmsIntegrationPanel } from "@/pages/stammdaten/schnittstellen/PmsIntegrationPanel";
import { SchnittstellenOverview } from "@/pages/stammdaten/schnittstellen/SchnittstellenOverview";
import { TouristIntegrationPanel } from "@/pages/stammdaten/schnittstellen/TouristIntegrationPanel";
import { TouristImportIntegrationPanel } from "@/pages/stammdaten/schnittstellen/TouristImportIntegrationPanel";
import { TseIntegrationPanel } from "@/pages/stammdaten/schnittstellen/TseIntegrationPanel";
import { useIntegrationStatus } from "@/pages/stammdaten/schnittstellen/useIntegrationStatus";
import { StripeIntegrationPanel } from "@/pages/stammdaten/schnittstellen/StripeIntegrationPanel";
import { ZvtIntegrationPanel } from "@/pages/stammdaten/schnittstellen/ZvtIntegrationPanel";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import {
  getIntegrationDef,
  legacySectionToIntegration,
  parseSchnittstellenIntegration,
  schnittstellenIntegrationHref,
  type SchnittstellenIntegrationId,
} from "@/pages/stammdaten/schnittstellenIntegrations";

function IntegrationPanel({
  integrationId,
  canWrite,
}: {
  integrationId: SchnittstellenIntegrationId;
  canWrite: boolean;
}) {
  switch (integrationId) {
    case "email":
      return <EmailIntegrationPanel canWrite={canWrite} />;
    case "pms":
      return <PmsIntegrationPanel canWrite={canWrite} />;
    case "tourist":
      return <TouristIntegrationPanel canWrite={canWrite} />;
    case "tourist-import":
      return <TouristImportIntegrationPanel canWrite={canWrite} />;
    case "google-places":
      return <GooglePlacesIntegrationPanel canWrite={canWrite} />;
    case "channel-api":
      return <ChannelApiIntegrationPanel canWrite={canWrite} />;
    case "tse":
      return <TseIntegrationPanel canWrite={canWrite} />;
    case "zvt":
      return <ZvtIntegrationPanel canWrite={canWrite} />;
    case "stripe":
      return <StripeIntegrationPanel canWrite={canWrite} />;
    case "device-service":
      return <DeviceServiceIntegrationPanel canWrite={canWrite} />;
    case "fernauslese":
      return <FernausleseIntegrationPanel canWrite={canWrite} />;
    case "barrier":
      return <BarrierIntegrationPanel canWrite={canWrite} />;
    case "lpr":
      return <LprIntegrationPanel canWrite={canWrite} />;
    case "events":
      return <InfoScreenProvidersPanel canWrite={canWrite} />;
  }
}

function IntegrationDetailPage({
  integrationId,
  canWrite,
}: {
  integrationId: SchnittstellenIntegrationId;
  canWrite: boolean;
}) {
  const { loading, status } = useIntegrationStatus(integrationId);
  const bare =
    integrationId === "device-service" ||
    integrationId === "events" ||
    integrationId === "fernauslese" ||
    integrationId === "tourist-import";

  return (
    <IntegrationPageLayout
      integrationId={integrationId}
      configured={status?.configured}
      statusDetail={status?.detail}
      loading={loading}
      bare={bare}
    >
      <IntegrationPanel integrationId={integrationId} canWrite={canWrite} />
    </IntegrationPageLayout>
  );
}

export function SchnittstellenPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);

  const integrationId =
    parseSchnittstellenIntegration(searchParams.get("integration")) ??
    legacySectionToIntegration(searchParams.get("section"));

  // Redirect legacy ?section= URLs to ?integration=
  useEffect(() => {
    const legacySection = searchParams.get("section");
    const currentIntegration = searchParams.get("integration");
    if (!currentIntegration && legacySection) {
      const mapped = legacySectionToIntegration(legacySection);
      if (mapped) {
        navigate(schnittstellenIntegrationHref(mapped), { replace: true });
      }
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (integrationId) {
      const def = getIntegrationDef(integrationId);
      setPageMeta({ title: "Stammdaten", subtitle: `Schnittstellen · ${def.shortLabel}` });
    } else {
      setPageMeta({ title: "Stammdaten", subtitle: "Schnittstellen" });
    }
    return () => setPageMeta(null);
  }, [integrationId, setPageMeta]);

  return (
    <div className="space-y-6">
      {!canWrite ? <StammdatenWriteHint /> : null}

      {integrationId ? (
        <IntegrationDetailPage integrationId={integrationId} canWrite={canWrite} />
      ) : (
        <SchnittstellenOverview />
      )}
    </div>
  );
}
